/* Align framed artwork in product cards without editing or replacing product images. */
(() => {
  'use strict';
  const states = new WeakMap();
  const scans = new Map();
  const fullImage = { x: 0, y: 0, width: 1, height: 1 };

  function detectArtworkBounds(pixels, width, height) {
    const colorAt = (x, y) => {
      const i = (y * width + x) * 4;
      return [pixels[i], pixels[i + 1], pixels[i + 2]];
    };
    const distance = (a, b) => Math.max(...a.map((channel, i) => Math.abs(channel - b[i])));
    const luminance = color => .2126 * color[0] + .7152 * color[1] + .0722 * color[2];
    const corners = [[0, 0], [width - 4, 0], [0, height - 4], [width - 4, height - 4]];
    const colors = corners.map(([left, top]) => {
      const channels = [[], [], []];
      for (let y = top; y < top + 4; y++) {
        for (let x = left; x < left + 4; x++) {
          colorAt(x, y).forEach((channel, i) => channels[i].push(channel));
        }
      }
      return channels.map(values => values.sort((a, b) => a - b)[8]);
    });
    const background = [0, 1, 2].map(i => colors.reduce((sum, color) => sum + color[i], 0) / 4);
    // Interior photos, dark backgrounds and uneven backgrounds keep the full image.
    if (luminance(background) < 215 || colors.some(color => distance(color, background) > 18)) return null;
    let borderPixels = 0;
    let lightBorderPixels = 0;
    for (let y = 0; y < height; y++) {
      for (const x of [0, width - 1]) {
        borderPixels++;
        if (distance(colorAt(x, y), background) < 25) lightBorderPixels++;
      }
    }
    for (let x = 0; x < width; x++) {
      for (const y of [0, height - 1]) {
        borderPixels++;
        if (distance(colorAt(x, y), background) < 25) lightBorderPixels++;
      }
    }
    if (lightBorderPixels / borderPixels < .9) return null;
    const rows = new Uint16Array(height);
    const columns = new Uint16Array(width);
    const mask = new Uint8Array(width * height);
    const redMin = background[0] - 55, redMax = background[0] + 55;
    const greenMin = background[1] - 55, greenMax = background[1] + 55;
    const blueMin = background[2] - 55, blueMax = background[2] + 55;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        if (pixels[i] < redMin || pixels[i] > redMax ||
            pixels[i + 1] < greenMin || pixels[i + 1] > greenMax ||
            pixels[i + 2] < blueMin || pixels[i + 2] > blueMax) {
          mask[y * width + x] = 1;
          rows[y]++;
          columns[x]++;
        }
      }
    }
    const left = columns.findIndex(count => count > height * .15);
    const top = rows.findIndex(count => count > width * .15);
    if (left < 0 || top < 0) return null;
    let right = width - 1;
    let bottom = height - 1;
    while (columns[right] <= height * .15) right--;
    while (rows[bottom] <= width * .15) bottom--;
    const artWidth = right - left + 1;
    const artHeight = bottom - top + 1;
    if (artWidth / width < .55 || artHeight / height < .55 ||
        artWidth * artHeight / (width * height) < .45) return null;
    // Require a dense rectangular frame on all four sides. Colorful details alone
    // must not cause a white painting or a room scene to be cropped.
    const horizontalEdge = y => {
      let count = 0;
      for (let x = left; x <= right; x++) count += mask[y * width + x];
      return count / artWidth;
    };
    const verticalEdge = x => {
      let count = 0;
      for (let y = top; y <= bottom; y++) count += mask[y * width + x];
      return count / artHeight;
    };
    if (Math.max(horizontalEdge(top), horizontalEdge(Math.min(top + 2, bottom))) < .75 ||
        Math.max(horizontalEdge(bottom), horizontalEdge(Math.max(bottom - 2, top))) < .75 ||
        Math.max(verticalEdge(left), verticalEdge(Math.min(left + 2, right))) < .75 ||
        Math.max(verticalEdge(right), verticalEdge(Math.max(right - 2, left))) < .75) return null;
    // A little surrounding space preserves the entire frame and its soft shadow.
    const padding = 3;
    const x = Math.max(0, left - padding);
    const y = Math.max(0, top - padding);
    return {
      x: x / width,
      y: y / height,
      width: (Math.min(width, right + padding + 1) - x) / width,
      height: (Math.min(height, bottom + padding + 1) - y) / height,
      frame: { x: left / width, y: top / height, width: artWidth / width, height: artHeight / height }
    };
  }

  function scanArtwork(source) {
    if (!source) return Promise.resolve(null);
    if (scans.has(source)) return scans.get(source);
    const scan = new Promise(resolve => {
      const sample = new Image();
      let finished = false;
      const finish = bounds => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        sample.onload = sample.onerror = null;
        resolve(bounds);
      };
      const timer = setTimeout(() => finish(null), 5000);
      sample.crossOrigin = 'anonymous';
      sample.decoding = 'async';
      sample.onload = () => {
        try {
          const scale = Math.min(1, 320 / Math.max(sample.naturalWidth, sample.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(8, Math.round(sample.naturalWidth * scale));
          canvas.height = Math.max(8, Math.round(sample.naturalHeight * scale));
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) { finish(null); return; }
          context.fillStyle = '#fff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(sample, 0, 0, canvas.width, canvas.height);
          finish(detectArtworkBounds(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height));
        } catch (_) {
          // A blocked CORS request or an unreadable canvas never hides an image.
          finish(null);
        }
      };
      sample.onerror = () => finish(null);
      sample.src = source;
    });
    scans.set(source, scan);
    return scan;
  }

  function previewGeometry(stageWidth, stageHeight, imageWidth, imageHeight, bounds, fill, zoom) {
    const artWidth = imageWidth * bounds.width;
    const artHeight = imageHeight * bounds.height;
    const fit = Math.min(stageWidth / artWidth, stageHeight / artHeight);
    // Even a manual enlargement must retain the complete detected artwork.
    const scale = fit * Math.min(.98, fill * zoom);
    return {
      width: imageWidth * scale / stageWidth * 100,
      height: imageHeight * scale / stageHeight * 100,
      left: ((stageWidth - artWidth * scale) / 2 - imageWidth * bounds.x * scale) / stageWidth * 100,
      top: ((stageHeight - artHeight * scale) / 2 - imageHeight * bounds.y * scale) / stageHeight * 100
    };
  }

  function positionArtwork(stage) {
    const state = states.get(stage);
    if (!state || !state.image.naturalWidth || !state.image.naturalHeight) return;
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (!width || !height) return;
    const fill = Math.max(.7, Math.min(.96, Number(stage.dataset.artworkFill) / 100 || .92));
    const zoom = Math.max(.5, Math.min(1.1, Number(stage.dataset.artworkZoom) / 100 || 1));
    const geometry = previewGeometry(width, height, state.image.naturalWidth, state.image.naturalHeight, state.bounds, fill, zoom);
    for (const [property, value] of Object.entries(geometry)) state.image.style[property] = `${value.toFixed(5)}%`;
    state.image.classList.add('is-artwork-positioned');
    const card = stage.closest('.art-card');
    if (card) {
      const frame = state.bounds.frame || state.bounds;
      const visibleLeft = geometry.left + frame.x * geometry.width;
      const visibleWidth = frame.width * geometry.width;
      const visibleBottom = geometry.top + (frame.y + frame.height) * geometry.height;
      card.style.setProperty('--art-card-info-left', `${visibleLeft.toFixed(5)}%`);
      card.style.setProperty('--art-card-info-width', `${visibleWidth.toFixed(5)}%`);
      card.style.setProperty('--art-card-bottom-space', `${Math.max(0, height * (1 - visibleBottom / 100)).toFixed(3)}px`);
    }
  }

  function normalizeArtwork(stage) {
    const state = states.get(stage);
    if (!state || state.scanned) return;
    state.scanned = true;
    if (stage.dataset.artworkAuto === 'false') {
      stage.dataset.artworkState = 'disabled';
      return;
    }
    scanArtwork(stage.dataset.artworkSource).then(bounds => {
      if (!stage.isConnected) return;
      state.bounds = bounds || fullImage;
      stage.dataset.artworkState = bounds ? 'normalized' : 'full-image';
      positionArtwork(stage);
    });
  }

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
    entries.forEach(entry => positionArtwork(entry.target));
  }) : null;
  const intersectionObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      intersectionObserver.unobserve(entry.target);
      normalizeArtwork(entry.target);
    });
  }, { rootMargin: '300px' }) : null;

  function initialize(root) {
    const stages = [...root.querySelectorAll('[data-artwork-preview]')];
    if (root.matches && root.matches('[data-artwork-preview]')) stages.unshift(root);
    stages.forEach(stage => {
      if (states.has(stage)) return;
      const image = stage.querySelector('.card-first-image');
      if (!image) return;
      states.set(stage, { image, bounds: fullImage, scanned: false });
      image.addEventListener('load', () => positionArtwork(stage));
      const hoverImage = stage.querySelector('.card-hover-image');
      if (hoverImage) {
        const updateHover = () => stage.classList.toggle('is-hover-ready', hoverImage.complete && hoverImage.naturalWidth > 0);
        hoverImage.addEventListener('load', updateHover);
        hoverImage.addEventListener('error', updateHover);
        updateHover();
      }
      positionArtwork(stage);
      if (resizeObserver) resizeObserver.observe(stage);
      if (intersectionObserver) intersectionObserver.observe(stage);
      else normalizeArtwork(stage);
    });
  }

  function start() {
    initialize(document);
    document.documentElement.classList.add('artwork-previews-enabled');
    document.addEventListener('shopify:section:load', event => initialize(event.target));
    if (!resizeObserver) window.addEventListener('resize', () => {
      document.querySelectorAll('[data-artwork-preview]').forEach(positionArtwork);
    });
    // Related motifs can be inserted by the existing recommendation loader.
    if (typeof MutationObserver === 'function') new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) initialize(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
